import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { AuthenticationService, LoginUserToken } from '../../authentication.service';
import { HTTP_STATUS_OFFLINE } from '../../../http/httpError';
import { LoadingDirective } from '@styleguide/loading/loading.directive';
import { ErrorsComponent } from '@styleguide/error/errors/errors.component';
import { FormFieldComponent } from '@styleguide/form/form-field/form-field.component';
import { CodeInputComponent } from '../code-input/code-input.component';
import { timer } from 'rxjs';

const TWO_FACTOR_TIMEOUT_MS = 3 * 60 * 1000;

@Component({
  selector: 'kgr-two-factor-form',
  templateUrl: './two-factor-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LoadingDirective, ReactiveFormsModule, ErrorsComponent, FormFieldComponent, CodeInputComponent],
})
export class TwoFactorFormComponent implements OnInit {
  @Input({ required: true }) twoFactorToken!: string;
  @Output() verifySuccess = new EventEmitter<LoginUserToken[]>();
  @Output() cancelVerify = new EventEmitter<void>();

  private destroyRef = inject(DestroyRef);

  isLoading = false;
  useBackupCode = false;
  errorInvalidCode = false;
  errorOffline = false;
  errorTooManyAttempts = false;
  unexpectedError = false;

  get codeLength(): number {
    return this.useBackupCode ? 8 : 6;
  }

  form = new UntypedFormGroup({
    code: new FormControl<string>('', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]),
    trustedDevice: new FormControl<boolean>(false),
  });

  get codeLabel(): string {
    return this.useBackupCode ? $localize`Backup-Code` : $localize`Authentifizierungscode`;
  }

  onToggleBackupCode(): void {
    this.useBackupCode = !this.useBackupCode;
    const codeControl = this.form.get('code')!;
    codeControl.setValue('');
    codeControl.setValidators([Validators.required, Validators.minLength(this.codeLength), Validators.maxLength(this.codeLength)]);
    codeControl.updateValueAndValidity();
    this.errorInvalidCode = false;
  }

  constructor(
    private authenticationService: AuthenticationService,
    private cd: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    timer(TWO_FACTOR_TIMEOUT_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onCancel());
  }

  onSubmit() {
    this.errorInvalidCode = false;
    this.errorOffline = false;
    this.errorTooManyAttempts = false;
    this.unexpectedError = false;

    if (this.form.invalid) {
      return;
    }

    this.isLoading = true;
    this.authenticationService.verify2FA(this.form.value.code, this.twoFactorToken, this.form.value.trustedDevice).subscribe({
      next: (loginUserTokens) => {
        this.isLoading = false;
        this.verifySuccess.emit(loginUserTokens);
        this.cd.markForCheck();
      },
      error: (apiError) => {
        switch (apiError.code) {
          case HTTP_STATUS_OFFLINE:
          case 0:
            this.errorOffline = true;
            break;
          case 400:
            this.errorInvalidCode = true;
            break;
          case 429:
            this.errorTooManyAttempts = true;
            break;
          default:
            this.unexpectedError = true;
            break;
        }
        this.isLoading = false;
        this.cd.markForCheck();
      },
    });
  }

  onCancel() {
    this.cancelVerify.emit();
  }
}
